import React, { useEffect, useMemo, useState } from 'react';
import { API_ENDPOINTS } from '../../config/apiConfig';
import { validateIndianPhoneNumber } from '../../utils/phoneFormatter';
import ConfirmationModal from '../../shared/components/modals/ConfirmationModal';
import OverrideBadge, { OverrideBadgeLegend } from '../../shared/components/common/OverrideBadge';
import { toast } from 'react-toastify';
import './DeveloperSettingsCard.css';

function toBool(v, fallback = false) {
  if (typeof v === 'boolean') return v;
  if (typeof v === 'string') return v.trim().toLowerCase() === 'true';
  if (typeof v === 'number') return v !== 0;
  return fallback;
}

const DEFAULTS = {
  developer_settings_enabled: false,
  sandbox_email: '',
  whatsapp_sandbox_phone_number: '',
  print_email_content_in_sandbox: false,
  print_msg_content_in_sandbox: false,
  whatsapp_send_template_message: true,
};

function DevSettingSectionCard({ title, icon, description, disabled, children }) {
  return (
    <section className="developer-settings-card">
      <div className="developer-settings-card-header">
        <h3 className="developer-settings-card-title">
          {icon ? (
            <span className="developer-settings-card-title-icon" aria-hidden="true">
              {icon}
            </span>
          ) : null}
          <span>{title}</span>
        </h3>
        {description ? <p className="developer-settings-card-desc">{description}</p> : null}
      </div>
      <div className={`developer-settings-card-body ${disabled ? 'disabled' : ''}`}>{children}</div>
    </section>
  );
}

function validateEmailOrEmpty(value) {
  const v = String(value || '').trim();
  if (v === '') return { isValid: true, error: null };
  // Reasonable email validation for UI (backend should still validate).
  // Avoids overly-strict regex.
  const ok = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
  return ok ? { isValid: true, error: null } : { isValid: false, error: 'Please enter a valid email address or leave it empty.' };
}

function validateWhatsappSandboxPhoneOrEmpty(value) {
  const v = String(value || '').trim();
  if (v === '') return { isValid: true, error: null };
  const digits = v.replace(/\D/g, '');

  // Accept either:
  // - 10-digit Indian mobile number (no country code)
  // - 12-digit number starting with 91 (India country code + 10 digits)
  if (digits.length === 10) {
    return validateIndianPhoneNumber(digits);
  }
  if (digits.length === 12 && digits.startsWith('91')) {
    return validateIndianPhoneNumber(digits.slice(2));
  }
  return {
    isValid: false,
    error: 'Enter an Indian mobile number as 10 digits, or with country code as 91XXXXXXXXXX. Leave empty for no redirect.',
  };
}

function displayEmptyableValue(v) {
  const s = String(v ?? '').trim();
  return s ? s : '(empty)';
}

function OverriddenBadge() {
  return <OverrideBadge title="Saved value differs from default" />;
}

function SandboxOverrideRow({
  label,
  envDefault,
  isOverride,
  error,
  inputType = 'text',
  inputClassName = '',
  value,
  disabled,
  onChange,
}) {
  const envStr = String(envDefault ?? '').trim();
  const valueStr = String(value ?? '');
  const matchesBaseline = valueStr.trim() === envStr;
  // No saved override and field still equals env → show Effective Runtime-style baseline cue.
  const showBaselineCue = !isOverride && matchesBaseline;
  const displayValue = showBaselineCue ? '' : valueStr;
  const inputPlaceholder =
    isOverride && !valueStr.trim() ? '(empty)' : showBaselineCue ? 'using default' : '(empty)';

  return (
    <div className="developer-settings-field">
      <div className="sandbox-override-row">
        <div className="sandbox-override-label">{label}</div>
        <span className="sandbox-override-pill sandbox-override-pill-baseline" title="Default">
          {displayEmptyableValue(envDefault)}
        </span>
        <span className="sandbox-override-arrow" aria-hidden="true">
          →
        </span>
        <span className="sandbox-override-value-wrap">
          <input
            type={inputType}
            value={displayValue}
            disabled={disabled}
            onChange={onChange}
            placeholder={inputPlaceholder}
            className={[
              'sandbox-override-input',
              showBaselineCue ? 'sandbox-override-input-baseline' : '',
              inputClassName,
              error ? 'invalid' : '',
            ]
              .filter(Boolean)
              .join(' ')}
          />
          {isOverride ? <OverriddenBadge /> : null}
        </span>
      </div>
      {error ? <div className="developer-settings-field-error">{error}</div> : null}
    </div>
  );
}

export default function DeveloperSettingsCard({
  token,
  onRuntimeSettingsUpdated,
  embedded = false,
  refreshTrigger = 0,
}) {
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState(DEFAULTS);
  const [envDefaults, setEnvDefaults] = useState(DEFAULTS);
  const [overrideKeys, setOverrideKeys] = useState([]);
  const [resetting, setResetting] = useState(false);
  const [resetModalOpen, setResetModalOpen] = useState(false);

  const headers = useMemo(
    () => ({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    }),
    [token]
  );

  const fetchSettings = async () => {
    setLoading(true);
    setError('');
    setForbidden(false);
    try {
      const res = await fetch(API_ENDPOINTS.DEV_SETTINGS.GET, { headers });
      if (res.status === 403) {
        setForbidden(true);
        setLoading(false);
        return;
      }
      const json = await res.json();
      if (!res.ok) {
        setError(json?.error || 'Failed to load developer settings');
        setLoading(false);
        return;
      }
      const nextEnvDefaults = {
        ...DEFAULTS,
        ...(json?.env_defaults || {}),
      };
      const nextOverrideKeys = Array.isArray(json?.override_keys) ? json.override_keys : [];
      const s = json?.settings || {};
      const boolSetting = (key, fallback = false) =>
        nextOverrideKeys.includes(key) ? toBool(s[key], toBool(nextEnvDefaults[key], fallback)) : toBool(nextEnvDefaults[key], fallback);
      const stringSetting = (key) =>
        nextOverrideKeys.includes(key) ? String(s[key] || '') : String(nextEnvDefaults[key] || '');

      setEnvDefaults(nextEnvDefaults);
      setOverrideKeys(nextOverrideKeys);
      setSettings({
        developer_settings_enabled: boolSetting('developer_settings_enabled', false),
        sandbox_email: stringSetting('sandbox_email'),
        whatsapp_sandbox_phone_number: stringSetting('whatsapp_sandbox_phone_number'),
        print_email_content_in_sandbox: boolSetting('print_email_content_in_sandbox', false),
        print_msg_content_in_sandbox: boolSetting('print_msg_content_in_sandbox', false),
        whatsapp_send_template_message: boolSetting('whatsapp_send_template_message', true),
      });
      setLoading(false);
    } catch (e) {
      setError(String(e));
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!refreshTrigger) return;
    fetchSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshTrigger]);

  const validation = useMemo(() => {
    return {
      sandbox_email: validateEmailOrEmpty(settings.sandbox_email),
      whatsapp_sandbox_phone_number: validateWhatsappSandboxPhoneOrEmpty(settings.whatsapp_sandbox_phone_number),
    };
  }, [settings.sandbox_email, settings.whatsapp_sandbox_phone_number]);

  const hasValidationErrors =
    !validation.sandbox_email.isValid || !validation.whatsapp_sandbox_phone_number.isValid;

  const hasSandboxEmailOverride = overrideKeys.includes('sandbox_email');
  const hasWhatsappSandboxPhoneOverride = overrideKeys.includes('whatsapp_sandbox_phone_number');
  const hasDeveloperSettingsEnabledOverride = overrideKeys.includes('developer_settings_enabled');
  const hasPrintEmailContentOverride = overrideKeys.includes('print_email_content_in_sandbox');
  const hasPrintMsgContentOverride = overrideKeys.includes('print_msg_content_in_sandbox');
  const hasWhatsappSendTemplateMessageOverride = overrideKeys.includes('whatsapp_send_template_message');

  const save = async () => {
    if (hasValidationErrors) {
      setError('Please fix validation errors before saving.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const res = await fetch(API_ENDPOINTS.DEV_SETTINGS.UPDATE, {
        method: 'PUT',
        headers,
        body: JSON.stringify({ settings }),
      });
      if (res.status === 403) {
        setForbidden(true);
        setError('Developer settings are disabled in this environment (403).');
        setSaving(false);
        return;
      }

      let json = null;
      try {
        json = await res.json();
      } catch (e) {
        json = null;
      }
      if (!res.ok) {
        const msg = json?.error || `Failed to save developer settings (HTTP ${res.status})`;
        setError(msg);
        toast.error(msg);
        setSaving(false);
        return;
      }
      // Re-fetch to ensure UI matches backend normalization + env defaults.
      await fetchSettings();
      await onRuntimeSettingsUpdated?.();
      toast.success('Developer settings saved.');
    } catch (e) {
      const msg = String(e);
      setError(msg);
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const resetToDefaults = async () => {
    setResetting(true);
    setError('');
    try {
      const res = await fetch(API_ENDPOINTS.DEV_SETTINGS.RESET, {
        method: 'POST',
        headers,
      });
      if (res.status === 403) {
        setForbidden(true);
        const msg = 'Developer settings are disabled in this environment (403).';
        setError(msg);
        toast.error(msg);
        return;
      }
      let json = null;
      try {
        json = await res.json();
      } catch (e) {
        json = null;
      }
      if (!res.ok) {
        const msg = json?.error || `Failed to reset developer settings (HTTP ${res.status})`;
        setError(msg);
        toast.error(msg);
        return;
      }
      await fetchSettings();
      await onRuntimeSettingsUpdated?.();
      toast.success('Developer settings reset to defaults.');
    } catch (e) {
      const msg = String(e);
      setError(msg);
      toast.error(msg);
    } finally {
      setResetting(false);
    }
  };

  if (loading) {
    return (
      <div className="developer-settings-page">
        <div className="developer-settings-state">Loading Developer Settings…</div>
      </div>
    );
  }

  if (forbidden) {
    return (
      <div className="developer-settings-page">
        <div className="developer-settings-state">Developer settings are disabled in this environment.</div>
      </div>
    );
  }

  return (
    <div className={`developer-settings-page ${embedded ? 'developer-settings-page-embedded' : ''}`}>
      {!embedded ? (
        <div className="developer-settings-header">
          <div className="developer-settings-header-left">
            <div className="developer-settings-header-icon" aria-hidden="true">
              🧪
            </div>
            <div>
              <h2 className="developer-settings-title">Developer Settings</h2>
              <p className="developer-settings-subtitle">Demo/test-only controls.</p>
            </div>
          </div>
        </div>
      ) : null}

      <div className="developer-settings-toolbar">
        <OverrideBadgeLegend text="Saved value differs from default" />
        <div className="developer-settings-actions action-palette">
          <button
            type="button"
            className="action-btn action-secondary"
            onClick={() => setResetModalOpen(true)}
            disabled={saving || resetting}
          >
            <span className="action-text">{resetting ? 'Resetting…' : 'Reset'}</span>
          </button>
          <button
            type="button"
            className="action-btn action-primary"
            onClick={save}
            disabled={saving || resetting || hasValidationErrors}
          >
            <span className="action-text">{saving ? 'Saving…' : 'Save'}</span>
          </button>
        </div>
      </div>

      {error && (
        <div className="developer-settings-error">{error}</div>
      )}

      <ConfirmationModal
        isOpen={resetModalOpen}
        entityName="Developer Settings"
        entityType="settings"
        action="reset"
        actionVerb="reset"
        customMessage="This will delete saved overrides from the database and revert to defaults."
        isLoading={resetting}
        onCancel={() => setResetModalOpen(false)}
        onConfirm={async () => {
          setResetModalOpen(false);
          await resetToDefaults();
        }}
      />

      <hr className="developer-settings-divider" />

      <div className="developer-settings-enable-row">
        <label className="developer-settings-toggle">
          <input
            type="checkbox"
            checked={settings.developer_settings_enabled}
            onChange={(e) => setSettings((p) => ({ ...p, developer_settings_enabled: e.target.checked }))}
          />
          <span className="developer-settings-toggle-label">
            Enable Developer Settings overrides{' '}
            <span className="developer-settings-default">
              (default: {envDefaults.developer_settings_enabled ? 'on' : 'off'})
              {hasDeveloperSettingsEnabledOverride ? (
                <>
                  {' '}
                  <OverriddenBadge />
                </>
              ) : null}
            </span>
          </span>
        </label>
        <span className="developer-settings-tip">
          Tip: leave sandbox targets empty to send to real recipients.
        </span>
      </div>

      <div className={`developer-settings-body ${settings.developer_settings_enabled ? '' : 'disabled'}`}>
        <div className="developer-settings-cards">
          <DevSettingSectionCard
            title="Email"
            icon="📧"
            disabled={!settings.developer_settings_enabled}
          >
            <label className="developer-settings-row">
              <input
                type="checkbox"
                checked={settings.print_email_content_in_sandbox}
                disabled={!settings.developer_settings_enabled}
                onChange={(e) => setSettings((p) => ({ ...p, print_email_content_in_sandbox: e.target.checked }))}
              />
              <span>
                Print email content in console{' '}
                <span className="developer-settings-default">
                  (default: {envDefaults.print_email_content_in_sandbox ? 'on' : 'off'})
                  {hasPrintEmailContentOverride ? (
                    <>
                      {' '}
                      <OverriddenBadge />
                    </>
                  ) : null}
                </span>
              </span>
            </label>

            <SandboxOverrideRow
              label="Sandbox email"
              envDefault={envDefaults.sandbox_email}
              isOverride={hasSandboxEmailOverride}
              error={validation.sandbox_email.isValid ? null : validation.sandbox_email.error}
              inputType="email"
              inputClassName="sandbox-override-input-email"
              value={settings.sandbox_email}
              disabled={!settings.developer_settings_enabled}
              onChange={(e) => setSettings((p) => ({ ...p, sandbox_email: e.target.value }))}
            />
          </DevSettingSectionCard>

          <DevSettingSectionCard
            title="WhatsApp"
            icon="💬"
            disabled={!settings.developer_settings_enabled}
          >
            <label className="developer-settings-row">
              <input
                type="checkbox"
                checked={settings.print_msg_content_in_sandbox}
                disabled={!settings.developer_settings_enabled}
                onChange={(e) => setSettings((p) => ({ ...p, print_msg_content_in_sandbox: e.target.checked }))}
              />
              <span>
                Print WhatsApp content in console{' '}
                <span className="developer-settings-default">
                  (default: {envDefaults.print_msg_content_in_sandbox ? 'on' : 'off'})
                  {hasPrintMsgContentOverride ? (
                    <>
                      {' '}
                      <OverriddenBadge />
                    </>
                  ) : null}
                </span>
              </span>
            </label>

            <label className="developer-settings-row">
              <input
                type="checkbox"
                checked={settings.whatsapp_send_template_message}
                disabled={!settings.developer_settings_enabled}
                onChange={(e) => setSettings((p) => ({ ...p, whatsapp_send_template_message: e.target.checked }))}
              />
              <span>
                Send template messages (otherwise text){' '}
                <span className="developer-settings-default">
                  (default: {envDefaults.whatsapp_send_template_message ? 'template' : 'text'})
                  {hasWhatsappSendTemplateMessageOverride ? (
                    <>
                      {' '}
                      <OverriddenBadge />
                    </>
                  ) : null}
                </span>
              </span>
            </label>

            <SandboxOverrideRow
              label="Sandbox WhatsApp phone"
              envDefault={envDefaults.whatsapp_sandbox_phone_number}
              isOverride={hasWhatsappSandboxPhoneOverride}
              error={
                validation.whatsapp_sandbox_phone_number.isValid
                  ? null
                  : validation.whatsapp_sandbox_phone_number.error
              }
              inputType="text"
              inputClassName="sandbox-override-input-phone"
              value={settings.whatsapp_sandbox_phone_number}
              disabled={!settings.developer_settings_enabled}
              onChange={(e) => {
                const digitsOnly = String(e.target.value || '').replace(/\D/g, '').slice(0, 12);
                setSettings((p) => ({ ...p, whatsapp_sandbox_phone_number: digitsOnly }));
              }}
            />
          </DevSettingSectionCard>
        </div>
      </div>
    </div>
  );
}

